import type { Tables } from '@/types_db';
import { DISPLAY_NAME_MAX_LENGTH } from './constants';

export type UserDetails = Tables<'user_public_counts'>;
export type Model = Tables<'models'>;
export type Tone = Tables<'tones'>;
export type Training = Tables<'trainings'>;
export type ToneCount = Tables<'tones_counts'>;
export type User = Tables<'users'>;
export type ToneCountWithUser = ToneCount & { user: User };
/** A related-tones hit: the tone card plus its embedding distance from the source model. */
export type RelatedTone = ToneCountWithUser & { distance: number };
export type Make = Tables<'makes'>;
export type Tag = Tables<'tags'>;
export type ToneAndChildren = ToneCountWithUser & {
  models: Array<
    Partial<Model> & {
      training: Partial<Training> | null;
    }
  >;
  user: Tables<'users'>;
  makes: Array<Partial<Make>>;
  tags: Array<Partial<Tag>>;
  /** Per-viewer flag from get_tone_with_models; false when there is no viewer. */
  is_favorite?: boolean;
};

export type ModelWithChildren = Model & { training: Training; user: User };

/**
 * Shape of each entry in `trainings.esrs`. The DB column is JSONB (an array of
 * these objects); use this type when reading/writing it so call sites don't
 * have to repeat the `as EsrEntry[]` cast.
 *   - max_value: float8, the size-bucket boundary
 *   - esr:       float8, ESR recorded for that bucket
 */
export type EsrEntry = { max_value: number; esr: number };

// Backward-compat alias for the canonical `TrackingProp` in lib/analytics/events.ts.
// New code should import `TrackingProp` directly.
export type { TrackingProp as TrackingEvent } from '@/lib/analytics/events';

export const getURL = (path: string = '') => {
  // Check if NEXT_PUBLIC_SITE_URL is set and non-empty. Set this to your site URL in production env.
  // @todo
  let url =
    process?.env?.NEXT_PUBLIC_SITE_URL && process.env.NEXT_PUBLIC_SITE_URL.trim() !== ''
      ? process.env.NEXT_PUBLIC_SITE_URL
      : // If not set, check for NEXT_PUBLIC_VERCEL_URL, which is automatically set by Vercel.
        process?.env?.NEXT_PUBLIC_VERCEL_URL && process.env.NEXT_PUBLIC_VERCEL_URL.trim() !== ''
        ? process.env.NEXT_PUBLIC_VERCEL_URL
        : // If neither is set, default to localhost for local development.
          'http://localhost:3000/';

  // Trim the URL and remove trailing slash if exists.
  url = url.replace(/\/+$/, '');
  // Make sure to include `https://` when not localhost.
  url = url.includes('http') ? url : `https://${url}`;
  // Ensure path starts without a slash to avoid double slashes in the final URL.
  path = path.replace(/^\/+/, '');

  // Concatenate the URL and the path.
  return path ? `${url}/${path}` : url;
};

export const toDateTime = (secs: number) => {
  var t = new Date(+0); // Unix epoch start.
  t.setSeconds(secs);
  return t;
};

export const calculateTrialEndUnixTimestamp = (trialPeriodDays: number | null | undefined) => {
  // Check if trialPeriodDays is null, undefined, or less than 2 days
  if (trialPeriodDays === null || trialPeriodDays === undefined || trialPeriodDays < 2) {
    return undefined;
  }

  const currentDate = new Date(); // Current date and time
  const trialEnd = new Date(currentDate.getTime() + (trialPeriodDays + 1) * 24 * 60 * 60 * 1000); // Add trial days
  return Math.floor(trialEnd.getTime() / 1000); // Convert to Unix timestamp in seconds
};

const toastKeyMap: { [key: string]: string[] } = {
  status: ['status', 'status_description'],
  error: ['error', 'error_description'],
};

/**
 * Builds a redirect URL that carries a toast in the query string. For code
 * that cannot reach the client toast store: server actions, route handlers,
 * and URLs baked into emails. `UrlToastBridge` shows the toast on landing and
 * scrubs `status`/`status_description`/`error`/`error_description` from the
 * URL; `disable_button` and `arbitraryParams` are left in place because they
 * are form/flow state the destination page reads.
 *
 * Client components must not use this to toast — call `toast()` from
 * `@/components/ui/Toasts/use-toast` (enforced by ESLint under components/).
 */
const getToastRedirect = (
  path: string,
  toastType: string,
  toastName: string,
  toastDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
): string => {
  const [nameKey, descriptionKey] = toastKeyMap[toastType];

  let redirectPath = `${path}?${nameKey}=${encodeURIComponent(toastName)}`;

  if (toastDescription) {
    redirectPath += `&${descriptionKey}=${encodeURIComponent(toastDescription)}`;
  }

  if (disableButton) {
    redirectPath += `&disable_button=true`;
  }

  if (arbitraryParams) {
    redirectPath += `&${arbitraryParams}`;
  }

  return redirectPath;
};

export const getStatusRedirect = (
  path: string,
  statusName: string,
  statusDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
) =>
  getToastRedirect(path, 'status', statusName, statusDescription, disableButton, arbitraryParams);

export const getErrorRedirect = (
  path: string,
  errorName: string,
  errorDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
) => getToastRedirect(path, 'error', errorName, errorDescription, disableButton, arbitraryParams);

export const validUsername = (username: string) => {
  // username can only contain letters, numbers, underscores or dashes
  // if it contains anything else, remove it and return the new username
  const cleanedUsername = username.replace(/[^a-zA-Z0-9-_]/g, '');
  // if username is empty, return a random string of 10 letters
  return cleanedUsername || Math.random().toString(36).substring(2, 15);
};

export const validUrlPathname = (pathname: string) => {
  // replace all spaces with dashes and convert to lowercase
  pathname = pathname.replace(/\s+/g, '-').toLowerCase();
  // pathname can only contain letters, numbers, underscores or dashes
  // if it contains anything else, remove it
  const cleanedPathname = pathname.replace(/[^a-zA-Z0-9-_]/g, '');
  // replace multiple consecutive dashes with a single dash
  const normalizedPathname = cleanedPathname.replace(/-+/g, '-');
  // if pathname is empty, return a random string of 10 letters
  return normalizedPathname || Math.random().toString(36).substring(2, 15);
};

export const toTonePathname = (tone: Partial<Tone> | Partial<ToneCount>) => {
  return `${validUrlPathname(tone.title || '')}-${tone.id}`;
};

export const getIdFromNameId = (nameId: string) => {
  // get the last part of the pathname (after the last dash)
  const lastPart = nameId.split('-').pop();
  return parseInt(lastPart || '-1');
};

export const timeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Constants for time units in seconds
  const MINUTE = 60;
  const HOUR = MINUTE * 60; // 3600 seconds
  const DAY = HOUR * 24; // 86400 seconds
  const WEEK = DAY * 7; // 604800 seconds
  const MONTH = WEEK * 4;
  const YEAR = MONTH * 12;

  // Determine the closest unit based on lower thresholds
  if (seconds < MINUTE * 60) {
    // Less than 60 minutes, show in minutes
    const minutes = Math.round(seconds / MINUTE);
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  } else if (seconds < HOUR * 24) {
    // Less than 24 hours, show in hours
    const hours = Math.floor(seconds / HOUR);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  } else if (seconds < DAY * 7) {
    // Less than 7 days, show in days
    const days = Math.floor(seconds / DAY);
    return days === 1 ? '1 day ago' : `${days} days ago`;
  } else if (seconds < WEEK * 4) {
    // Less than 4 weeks, show in weeks
    const weeks = Math.floor(seconds / WEEK);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  } else if (seconds < MONTH * 12) {
    // Less than 12 months, show in months
    const months = Math.floor(seconds / MONTH);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  } else {
    // Show in years
    const years = Math.round(seconds / YEAR);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }
};

// Derived from now(): render via <TimeAgo>, never directly in a client component.
export const timeAgoShort = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;
  const WEEK = DAY * 7;
  const MONTH = WEEK * 4;
  const YEAR = MONTH * 12;
  if (seconds < 10) return 'now';
  if (seconds < MINUTE) return `${seconds}s`;
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h`;
  if (seconds < WEEK) return `${Math.floor(seconds / DAY)}d`;
  if (seconds < MONTH) return `${Math.floor(seconds / WEEK)}w`;
  if (seconds < YEAR) return `${Math.floor(seconds / MONTH)}mo`;
  return `${Math.floor(seconds / YEAR)}y`;
};

export const roundFloat = (num: number, decimalPlaces: number) => {
  return Math.round(num * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

export const dashToSpaceUpper = (str: string) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const progressFromStatus = (status: string) => {
  // if status text is in the form of "Epoch 20 of 100", return the progress as a number from 0 to 100. The total number of epochs is the last number in the string.
  const epochMatch = status.match(/Epoch (\d+) of (\d+)/);
  if (epochMatch) {
    const [, current, total] = epochMatch;
    return Math.round((parseInt(current) / parseInt(total)) * 100);
  }
  return 0;
};

export const sessionStartTs = Date.now();

export const estimateWaveNetParams = (config: {
  head_scale: number;
  layers_configs: {
    input_size: number;
    condition_size: number;
    channels: number;
    kernel_size: number;
    dilations: number[];
    head_size: number;
    head_bias: boolean;
    gated: boolean;
  }[];
}) => {
  // check required fields
  if (!config.layers_configs || !Array.isArray(config.layers_configs)) {
    throw new Error('Invalid config: layers_configs is required and must be an array');
  }
  // check at least one layer
  if (config.layers_configs.length === 0) {
    throw new Error('Invalid config: at least one layer is required');
  }
  // check all layers
  for (const layer of config.layers_configs) {
    if (!layer.input_size) {
      throw new Error('Invalid config: input_size is required');
    }
    if (!layer.condition_size) {
      throw new Error('Invalid config: condition_size is required');
    }
    if (!layer.channels) {
      throw new Error('Invalid config: channels is required');
    }
    if (!layer.kernel_size) {
      throw new Error('Invalid config: kernel_size is required');
    }
    if (!layer.dilations) {
      throw new Error('Invalid config: dilations is required');
    }
    if (!layer.head_size) {
      throw new Error('Invalid config: head_size is required');
    }
    if (typeof layer.head_bias !== 'boolean') {
      throw new Error('Invalid config: head_bias must be a boolean');
    }
    if (typeof layer.gated !== 'boolean') {
      throw new Error('Invalid config: gated must be a boolean');
    }
  }
  // check if dilations is an array of numbers
  if (
    !Array.isArray(config.layers_configs[0].dilations) ||
    !config.layers_configs[0].dilations.every((dilation) => typeof dilation === 'number')
  ) {
    throw new Error('Invalid config: dilations must be an array of numbers');
  }

  // check if kernel_size is a number
  if (typeof config.layers_configs[0].kernel_size !== 'number') {
    throw new Error('Invalid config: kernel_size must be a number');
  }

  // check if head_size is a number
  if (typeof config.layers_configs[0].head_size !== 'number') {
    throw new Error('Invalid config: head_size must be a number');
  }

  // check if head_bias is a boolean
  if (typeof config.layers_configs[0].head_bias !== 'boolean') {
    throw new Error('Invalid config: head_bias must be a boolean');
  }

  // check if gated is a boolean
  if (typeof config.layers_configs[0].gated !== 'boolean') {
    throw new Error('Invalid config: gated must be a boolean');
  }

  // check if dilations is an array of numbers
  if (
    !Array.isArray(config.layers_configs[0].dilations) ||
    !config.layers_configs[0].dilations.every((dilation) => typeof dilation === 'number')
  ) {
    throw new Error('Invalid config: dilations must be an array of numbers');
  }

  // check if input_size is a number
  if (typeof config.layers_configs[0].input_size !== 'number') {
    throw new Error('Invalid config: input_size must be a number');
  }

  // check if condition_size is a number
  if (typeof config.layers_configs[0].condition_size !== 'number') {
    throw new Error('Invalid config: condition_size must be a number');
  }

  // check if channels is a number
  if (typeof config.layers_configs[0].channels !== 'number') {
    throw new Error('Invalid config: channels must be a number');
  }

  // check if head_size is a number
  if (typeof config.layers_configs[0].head_size !== 'number') {
    throw new Error('Invalid config: head_size must be a number');
  }

  // check if head_scale is a number
  if (typeof config.head_scale !== 'number') {
    throw new Error('Invalid config: head_scale must be a number');
  }

  let totalParams = 0;
  let layerDetails: {
    layer: number;
    rechannelParams: number;
    rechannelBias: number;
    layerParams: number;
    layerBias: number;
    headRechannelParams: number;
    headBias: number;
    totalLayerParams: number;
  }[] = [];

  config.layers_configs.forEach((layer, index) => {
    let {
      input_size,
      condition_size,
      channels,
      kernel_size,
      dilations,
      head_size,
      head_bias,
      gated,
    } = layer;

    // Rechanneling layer: Adjusts input size to match channel count
    let rechannelParams = input_size * channels * 1; // 1x1 conv (input_size -> channels)
    let rechannelBias = channels; // Bias for rechanneling conv

    // Per-dilation layers (each dilation applies the same set of convolutions)
    let layerParams = 0;
    let layerBias = 0;
    dilations.forEach(() => {
      let convChannels = gated ? 2 * channels : channels;
      let convParams = channels * convChannels * kernel_size; // Main convolution
      let convBias = convChannels; // Bias terms for main convolution

      let inputMixerParams = condition_size * convChannels * 1; // 1x1 conv
      let inputMixerBias = convChannels; // Bias for input mixer

      let oneByOneParams = channels * channels * 1; // 1x1 conv
      let oneByOneBias = channels; // Bias for 1x1 conv

      layerParams += convParams + inputMixerParams + oneByOneParams;
      layerBias += convBias + inputMixerBias + oneByOneBias;
    });

    // Head rechanneling: Maps channels to head size
    let headRechannelParams = channels * head_size * 1; // 1x1 conv
    let headBias = head_bias ? head_size : 0; // Bias for head conv

    // Total for this layer
    let totalLayerParams =
      rechannelParams + layerParams + headRechannelParams + rechannelBias + layerBias + headBias;
    totalParams += totalLayerParams;

    layerDetails.push({
      layer: index + 1,
      rechannelParams,
      rechannelBias,
      layerParams,
      layerBias,
      headRechannelParams,
      headBias,
      totalLayerParams,
    });
  });

  return { totalParams, layerDetails };
};

export const dashToSpaceUpperFirst = (str: string) => {
  if (!str) return '';
  str = str.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  if (str === 'Ir') return 'IR';
  return str;
};

export const areAllExtensionsConsistent = (files: { name: string }[]) => {
  if (files.length === 0) return false;
  const extensions = files.map((file) => file.name.split('.').pop()?.toLowerCase());
  return extensions.every((extension) => extension === extensions[0]);
};

export const pluralize = (word: string, count: number) => {
  return count === 1 ? word : `${word}s`;
};

export const cleanUsername = (username: string) => {
  return username.toLowerCase().replace(/[^a-z0-9]/g, '');
};

export const cleanDisplayName = (displayName: string) => {
  return displayName.trim().replace(/\s+/g, ' ').slice(0, DISPLAY_NAME_MAX_LENGTH);
};

interface NamedCreator {
  username?: string | null;
  display_name?: string | null;
  is_verified?: boolean | null;
}

/**
 * A display name is public only for a verified creator. The site and the API
 * both derive it here, so an unverified name never renders and never ships.
 */
export const publicDisplayName = (creator: Omit<NamedCreator, 'username'>): string | null =>
  creator.is_verified ? creator.display_name || null : null;

/** The name to render for a creator: their display name if public, else username. */
export const creatorName = (creator: NamedCreator): string =>
  publicDisplayName(creator) ?? creator.username ?? '';

export const sanitizeFilename = (filename: string) => {
  // Invalid characters for Windows/macOS/Linux
  const invalidChars = /[<>:"/\\|?*\x00-\x1F]/g; // Includes control characters
  const reservedNames = [
    'CON',
    'PRN',
    'AUX',
    'NUL',
    'COM1',
    'COM2',
    'COM3',
    'COM4',
    'COM5',
    'COM6',
    'COM7',
    'COM8',
    'COM9',
    'LPT1',
    'LPT2',
    'LPT3',
    'LPT4',
    'LPT5',
    'LPT6',
    'LPT7',
    'LPT8',
    'LPT9',
  ];

  // Remove invalid characters
  let cleanName = filename.replace(invalidChars, '_');

  // Trim spaces and periods (Windows does not allow trailing periods)
  cleanName = cleanName.trim().replace(/\.+$/, '');

  // Prevent empty filenames
  if (cleanName.length === 0) {
    cleanName = 'untitled';
  }

  // Check for reserved Windows filenames (case insensitive)
  if (reservedNames.includes(cleanName.toUpperCase())) {
    cleanName = '_' + cleanName; // Prefix to avoid conflict
  }

  // Enforce max length (Windows limit: 255 characters)
  if (cleanName.length > 255) {
    cleanName = cleanName.substring(0, 255);
  }

  return cleanName;
};

/**
 * Zip entry name for a model file: sanitized model name + extension from the
 * stored URL, deduped against `usedNames` with an incrementing suffix.
 * Shared by the client-side tone download (utils/download.ts) and the server
 * zip route (/api/v1/tones/[id]/download) so both produce identical archives.
 * Mutates `usedNames` by adding the returned name.
 */
export const uniqueZipEntryName = (
  modelName: string | null | undefined,
  modelUrl: string,
  usedNames: Set<string>
): string => {
  const extension = modelUrl.split('.').pop() || 'nam';
  const baseName = sanitizeFilename(modelName || 'model');
  let fileName = `${baseName}.${extension}`;
  let counter = 1;

  while (usedNames.has(fileName)) {
    fileName = `${baseName}_${counter}.${extension}`;
    counter++;
  }

  usedNames.add(fileName);
  return fileName;
};

/**
 * Zip entry name keyed by model id: `{id}.{ext}` from the stored URL.
 * Used by GET /api/v1/tones/{id}/download?filenames=id so zip entries can
 * be joined back to List Models rows.
 */
export const idZipEntryName = (modelId: number, modelUrl: string): string => {
  const extension = modelUrl.split('.').pop() || 'nam';
  return `${modelId}.${extension}`;
};

export const isYoutubeLink = (link: string) => {
  return (
    link.includes('https://www.youtube.com/watch') ||
    link.includes('https://youtu.be') ||
    link.includes('https://www.youtube.com/embed')
  );
};
